import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteConfig } from '@/mocks/siteConfig';

export default function CategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState<string | null>(slug || null);

  useEffect(() => {
    if (slug && categories.some((c) => c.slug === slug)) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (slug) {
      setActiveSlug(null);
    } else {
      setActiveSlug(null);
    }
  }, [slug]);

  const filteredArticles = useMemo(() => {
    if (!activeSlug) return articles;
    return articles.filter((a) => a.category === activeSlug);
  }, [activeSlug]);

  const activeCategory = activeSlug
    ? categories.find((c) => c.slug === activeSlug)
    : null;

  const handleCategoryClick = (catSlug: string) => {
    setActiveSlug(catSlug);
    navigate(`/category/${catSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShowAll = () => {
    setActiveSlug(null);
    navigate('/category');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageTitle = activeCategory
    ? `${activeCategory.name} - ${siteConfig.name}`
    : `视频分类 - ${siteConfig.name}`;
  const pageDesc = activeCategory
    ? `${activeCategory.name}分类，${activeCategory.description}。${siteConfig.name}为您精选${activeCategory.name}相关的优质视频内容与文章推荐。`
    : `浏览${siteConfig.name}的全部视频分类，包括${categories.map(c => c.name).join('、')}等八大领域，发现您感兴趣的视频内容。`;
  const pageKeywords = activeCategory
    ? `${activeCategory.name},${activeCategory.name}视频,视频分类,${siteConfig.keywords}`
    : `视频分类,视频导航,${siteConfig.keywords}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: activeCategory ? `${activeCategory.name} - 视频分类` : '视频分类',
    description: pageDesc,
    url: activeCategory
      ? `${siteConfig.url}/category/${activeCategory.slug}`
      : `${siteConfig.url}/category`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={
          activeCategory
            ? `${siteConfig.url}/category/${activeCategory.slug}`
            : `${siteConfig.url}/category`
        }
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main className="pt-20">
        <Breadcrumb
          items={
            activeCategory
              ? [
                  { label: '首页', path: '/' },
                  { label: '视频分类', path: '/category' },
                  { label: activeCategory.name },
                ]
              : [
                  { label: '首页', path: '/' },
                  { label: '视频分类' },
                ]
          }
        />

        <div className="w-full px-4 md:px-6 pb-14">
          <div className="max-w-7xl mx-auto">
            <div className="mb-10">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                {activeCategory ? activeCategory.name : '全部分类'}
              </h1>
              <p className="text-sm text-secondary-500">
                {activeCategory
                  ? activeCategory.description
                  : '探索八大视频分类，发现全网优质视频内容'}
              </p>
            </div>

            <div className="flex items-center gap-2 mb-8 flex-wrap">
              <button
                onClick={handleShowAll}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  !activeSlug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-secondary-600 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryClick(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-secondary-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.slug} {...article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-secondary-400">
                  <i className="ri-inbox-line text-4xl"></i>
                </div>
                <p className="text-secondary-500">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}